import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { LogIn, Search, Trash2, RefreshCw, Calendar, User } from "lucide-react";
import { format } from "date-fns";
import { sl } from "date-fns/locale";

const AccessLogsPage = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const limit = 50;

  useEffect(() => {
    fetchLogs();
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/access-logs?limit=${limit}&skip=${page * limit}`, { headers });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Napaka pri nalaganju logov");
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.delete(`${API}/access-logs/archive?days_old=90`, { headers });
      toast.success(response.data.message);
      fetchLogs();
      setArchiveDialogOpen(false);
    } catch (error) {
      toast.error("Napaka pri arhiviranju");
    }
  };

  const formatDateTime = (isoString) => {
    try {
      const date = new Date(isoString);
      return format(date, "dd.MM.yyyy HH:mm", { locale: sl });
    } catch {
      return isoString;
    }
  };

  const filteredLogs = logs.filter(log => {
    const searchLower = search.toLowerCase();
    return (
      log.ime?.toLowerCase().includes(searchLower) ||
      log.priimek?.toLowerCase().includes(searchLower) ||
      log.vloga?.toLowerCase().includes(searchLower)
    );
  });

  const getRoleBadgeColor = (role) => {
    const colors = {
      "administrator": "bg-purple-100 text-purple-700",
      "predsednik": "bg-green-100 text-green-700",
      "gospodar": "bg-blue-100 text-blue-700",
      "tajnik": "bg-yellow-100 text-yellow-700",
      "blagajnik": "bg-orange-100 text-orange-700",
      "clan": "bg-gray-100 text-gray-700"
    };
    return colors[role] || "bg-gray-100 text-gray-700";
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="access-logs-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Beleženje dostopov</h1>
          <p className="text-muted-foreground">
            Skupaj {total} zapisov prijav
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchLogs}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Osveži
          </Button>
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={() => setArchiveDialogOpen(true)}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Arhiviraj stare
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Išči po imenu, priimku, vlogi..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-sm"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="spinner" />
            </div>
          ) : (
            <>
              <ScrollArea className="h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Datum in čas</TableHead>
                      <TableHead>Član</TableHead>
                      <TableHead>Vloga</TableHead>
                      <TableHead>Tip</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          Ni zapisov
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLogs.map((log) => (
                        <TableRow key={log.id || log.cas}>
                          <TableCell className="whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-muted-foreground" />
                              {formatDateTime(log.cas)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-muted-foreground" />
                              {log.ime} {log.priimek}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={getRoleBadgeColor(log.vloga)}>
                              {log.vloga}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="flex items-center gap-1 w-fit">
                              <LogIn className="h-3 w-3" />
                              {log.tip}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Prikazanih {Math.min((page + 1) * limit, total)} od {total}
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                  >
                    Prejšnja
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={(page + 1) * limit >= total}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Naslednja
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Archive Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Arhiviraj stare zapise?</AlertDialogTitle>
            <AlertDialogDescription>
              To bo izbrisalo vse zapise prijav starejše od 90 dni. 
              Ta akcija je nepovratna.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Prekliči</AlertDialogCancel>
            <AlertDialogAction onClick={handleArchive} className="bg-destructive text-destructive-foreground">
              Arhiviraj
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AccessLogsPage;
